import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation ,Link} from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FiChevronDown, FiRefreshCw } from 'react-icons/fi';

const SubCategory = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const categoryId = searchParams.get("categoryId") || null;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subcategories, setSubcategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const refreshData = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SUBCATEGORY_LIST}?categoryId=${categoryId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch subcategories');
      }

      const result = await response.json();

      if (result.success && result.data && result.data.subcategories) {
  
        const formatMinutesToDaysHours = (minutes) => {
          const totalHours = minutes / 60;
          const days = Math.floor(totalHours / 24);
          const hours = Math.round(totalHours % 24);
  
          let result = '';
          if (days > 0) result += `${days} day${days > 1 ? 's' : ''}`;
          if (hours > 0) result += `${days > 0 ? ' ' : ''}${hours} hr`;
          if (result === '') result = '0 hr';
          return result;
        };
  
        const formatted = result.data.subcategories.map(item => ({
          id: item.id,
          categoryId: item.categoryId,
          description: item.description,
          categoryName: item.categoryName,
          name: item.name,
          slaMinutes: formatMinutesToDaysHours(item.slaMinutes),
          owners: item.owners,
          escalations: item.escalations,
          isEnabled: item.isEnabled,
          ownerNames: item.owners.map(o => o.notifyUserName).join(', '),
          escalationInfo: item.escalations
            ? item.escalations
                .map(e => `${formatMinutesToDaysHours(e.triggerMinutes)} - ${e.notifyUserName}`)
                .join(', ')
            : '',
        }));

        const ownersList = formatted.flatMap(subcat =>
          subcat.owners.map(owner => owner.notifyUserName)
        );
        setSubcategories(formatted);
      } else {
        throw new Error(result.success_message || 'Unexpected API response');
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: err.message || 'Failed to load subcategories.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshData();
  }, []);

  const handleEdit = (event, subcategoryId) => {
    const url = `/SubCategoryCreation?categoryId=${categoryId}&subcategoryId=${subcategoryId}`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };
  
  const handleDelete = (id) => {
    Swal.fire({
      title: 'Delete this subcategory?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it',
    }).then(async (res) => {
      if (res.isConfirmed) {
        try {
          const response = await axios.post(
            `${process.env.REACT_APP_API_SUBCATEGORY_DELETE}?ids=${id}`,
            null, {
            headers: {
              'Content-Type': 'application/json',
              'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
            }
          });

          if (response.data.success) {
            setSubcategories((prev) => prev.filter((s) => s.id !== id));
            Swal.fire({
              icon: 'success',
              title: 'Deleted!',
              toast: true,
              position: 'top-end',
              showConfirmButton: false,
              timer: 3000,
            });
          } else {
            Swal.fire({
              icon: 'error',
              title: 'Error!',
              text: response.data.message || 'Failed to delete.',
              toast: true,
              position: 'top-end',
              showConfirmButton: false,
              timer: 3000,
            });
          }
        } catch (err) {
          Swal.fire('Error!', 'Something went wrong.', 'error');
        }
      }
    });
  };

  const handleNewSubCategory = (event) => {
    const url = `/SubCategoryCreation?categoryId=${categoryId}`;
    if (event?.metaKey || event?.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };

  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const sorted = [...subcategories].sort((a, b) => {
    if (!sortConfig.key) return 0;
    let aVal = a[sortConfig.key], bVal = b[sortConfig.key];
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }
    return (aVal < bVal ? -1 : 1) * (sortConfig.direction === 'asc' ? 1 : -1);
  });

  const filtered = sorted.filter((s) =>
    Object.values(s)
      .flatMap((v) => Array.isArray(v) ? v : [v])
      .join(' ')
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const totalEntries = filtered.length;
  const indexOfFirstRow = (currentPage - 1) * rowsPerPage;
  const currentRows = filtered.slice(indexOfFirstRow, indexOfFirstRow + rowsPerPage);
  const totalPages = Math.ceil(totalEntries / rowsPerPage);
  const indexOfFirstEntry = totalEntries > 0 ? indexOfFirstRow + 1 : 0;
  const indexOfLastEntry = totalEntries > 0 ? Math.min(currentPage * rowsPerPage, totalEntries) : 0;

  const handlePageClick = (page) => setCurrentPage(page);
  const handleNextPage = () => { if (currentPage < totalPages) setCurrentPage((prev) => prev + 1); };
  const handlePrevPage = () => { if (currentPage > 1) setCurrentPage((prev) => prev - 1); };
  const handleItemsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const generatePageNumbers = () => {
    const pages = [];
    const totalPagesToShow = 5;
    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const renderPagination = () =>
    generatePageNumbers().map((page, index) =>
      typeof page === 'number' ? (
        <button
          key={index}
          className={`page-number ${currentPage === page ? 'active' : ''}`}
          onClick={() => handlePageClick(page)}
        >
          {page}
        </button>
      ) : (
        <span key={index} className="pagination-dots">{page}</span>
      )
    );

  return (
    <div className="enrollment-container">
        <div className="breadcrumb">
        <Link to="/ticketcategory" className="breadcrumb-link">
        Category List
        </Link>{' '}
        &gt; Sub Category List
      </div>
      <div className="search-bar">
        <div className="input-container">
          <input
            type="text"
            id="searchInput"
            placeholder=""
            className={`search-input ${searchTerm ? "has-value" : ""}`}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <label htmlFor="searchInput" className="floating-label">🔍 Search</label>
        </div>
        <button className="create-button-new" onClick={(e) => handleNewSubCategory(e)}>
          <span className="create-button__icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="23" height="23">
              <path d="M12 5v14M5 12h14" stroke="white" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <span className="create-button__label">Add SubCategory</span>
        </button>
      </div>

      <div className="items-and-dues">
        <select className="items-per-page-select" value={rowsPerPage} onChange={handleItemsPerPageChange}>
          <option value={10}>10 items per page</option>
          <option value={50}>50 items per page</option>
          <option value={75}>75 items per page</option>
        <option value={100}>100 items per page</option>
      </select>
      <div className="pagination-selector">
          <div className="button-group">
            <div className="group-label">Items per page:</div>
            <div className="button-options">
              {[10, 50, 75, 100].map((option) => (
                <button
                  key={option}
                  onClick={() =>
                    handleItemsPerPageChange({ target: { value: option } })
                  }
                  className={`page-button ${rowsPerPage === option ? 'active' : ''}`}
                >
                  <span className="button-text">{option}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <button className="tt-action-btn" onClick={refreshData}>
          <FiRefreshCw className={`refresh-icon ${loading ? 'spin' : ''}`} />
          Refresh
        </button>
      </div>
      
      <div className="tt-table-container">
        {loading ? (
          <div className="tt-loading"><div className="tt-loading-spinner"></div><p>Loading Subcategories...</p></div>
        ) : (
          <table className="tt-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('name')}>
                  <div className="th-content">
                    <span>Name</span>
                    <FiChevronDown className={`sort-icon ${sortConfig.key === 'name' ? 'active' : ''} ${sortConfig.direction === 'desc' ? 'desc' : ''}`} />
                  </div>
                </th>
                <th>
                  <div className="th-content">
                    <span>Owner(s)</span>
                  </div>
                </th>
                <th onClick={() => handleSort('slaMinutes')}>
                  <div className="th-content">
                    <span>SLA</span>
                    <FiChevronDown className={`sort-icon ${sortConfig.key === 'slaMinutes' ? 'active' : ''} ${sortConfig.direction === 'desc' ? 'desc' : ''}`} />
                  </div>
                </th>
                <th>
                  <div className="th-content">
                    <span>Escalations</span>
                  </div>
                </th>
                <th><div className="th-content"><span>Description</span></div></th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {currentRows.length > 0 ? currentRows.map(s => (
                <tr key={s.id}>
                  <td>{s.name}</td>
                  <td>{s.ownerNames}</td>
                  <td>{s.slaMinutes}</td>
                  <td>{s.escalationInfo}</td>
                  <td>{s.description}</td>
                  <td className="action-buttons">
                    <button className="edit-btn tooltip-btn"  onClick={(event) => handleEdit(event, s.id)}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 3.487a2.12 2.12 0 0 1 3.001 3.001L7.5 18.85l-4 1 1-4L16.862 3.487z" />
                      </svg>
                      <span className="tooltip-text">Edit</span>
                    </button>
                    <button className="delete-btn tooltip-btn" onClick={() => handleDelete(s.id)}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M10 11v6M14 11v6M5 7h14l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 7z" />
                      </svg>
                      <span className="tooltip-text">Delete</span>
                    </button>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan="4" style={{ textAlign: "center" }}>No SubCategories Found</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="pagination">
        <div className="showing-entries">
          Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
        </div>
        <button className="prev-button" onClick={handlePrevPage} disabled={currentPage === 1}>Previous</button>
        {renderPagination()}
        <button className="next-button" onClick={handleNextPage} disabled={currentPage === totalPages}>Next</button>
      </div>
    </div>
  );
};

export default SubCategory;
